import {
  OnboardingContext,
  OnboardingModule,
  OnboardingModuleCreateAccount
} from "../types";
import { WSQueryResult } from "@ws-react-query";
import { WSServiceError } from "../../../utils/serviceHelper";
import { WSFlexBox, WSLoader } from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { FlowStep } from "../../../shared/components/FlowSwitch/types";

const StepLoading = () => (
  <WSFlexBox.Center py="3XL">
    <WSLoader.Spinner size="M" />
  </WSFlexBox.Center>
);

export const loadStep = (
  step: FlowStep | null,
  ...dependencies: WSQueryResult<any, WSServiceError>[]
): FlowStep | null => {
  if (step === null) return null;

  if (dependencies.every(query => query.isFetched)) {
    return {
      ...step
    };
  }

  return {
    ...step,
    component: StepLoading,
    status: "Loading"
  };
};

export type OnboardingModuleType = OnboardingModule["type"];

export type UseCreateOnboardingStep<
  M extends OnboardingModule = OnboardingModuleCreateAccount
> = (
  modules: OnboardingModule[]
) => (module: M, ctx: OnboardingContext) => FlowStep | null;

export const useOnboardingStepQueryConfig = (
  modules: OnboardingModule[],
  type: OnboardingModuleType
) =>
  useMemo(
    () => ({ enabled: modules.some(m => m.type === type) }),
    [modules, type]
  );

export const buildOnboardingStep = <
  Module,
  Context,
  Data extends readonly unknown[]
>({
  getConfig,
  module,
  context,
  queries,
  loader = true
}: {
  getConfig: (
    module: Module,
    context: Context,
    ...data: Data
  ) => FlowStep | null;
  module: Module;
  context: Context;
  queries: {
    [K in keyof Data]-?: WSQueryResult<NonNullable<Data[K]>, WSServiceError>;
  };
  loader?: boolean;
}): FlowStep | null => {
  const data = queries.map(q => q.data) as unknown as Data;
  const config = getConfig(module, context, ...data);

  return loader ? loadStep(config, ...queries) : config;
};
